import type { CampaignReportResponse } from "../types";
import type {
  BrandShareMetric,
  ExportTemplateContext,
  ExportTemplateDefinition,
  ExportTemplateModule,
  TrendLineMetric,
} from "./types";
import { exportTemplates, getExportTemplate } from "./templateRegistry";
import {
  type CampaignVisualizationSpec,
  type JsonObject,
  validateCampaignVisualizationSpec,
} from "./visualizationSpec";

export interface WidescreenExportRenderPayload {
  data: CampaignReportResponse;
  template: ExportTemplateDefinition;
  callouts?: string;
  metadata?: ExportTemplateContext["metadata"];
}

export interface BrowserSavedReportRenderInput {
  savedReport: {
    data: unknown;
    callouts?: string;
    metadata?: ExportTemplateContext["metadata"];
  };
  templateId?: string;
}

export interface ArtifactRenderPayloadInput {
  report: unknown;
  spec: unknown;
}

export interface ExportRenderValidationError {
  path: string;
  code: string;
  message: string;
}

export type ExportRenderPayloadResult =
  | { ok: true; payload: WidescreenExportRenderPayload }
  | { ok: false; errors: ExportRenderValidationError[] };

export function buildBrowserSavedReportRenderPayload({
  savedReport,
  templateId,
}: BrowserSavedReportRenderInput): ExportRenderPayloadResult {
  const report = savedReport.data;
  if (!isCampaignReportResponse(report)) {
    return {
      ok: false,
      errors: [
        {
          path: "savedReport.data",
          code: "unsupported_report",
          message: "Widescreen export currently supports campaign uploads only.",
        },
      ],
    };
  }

  return {
    ok: true,
    payload: {
      data: report,
      template: getExportTemplate(templateId),
      callouts: savedReport.callouts,
      metadata: savedReport.metadata,
    },
  };
}

export function buildArtifactRenderPayload(
  input: ArtifactRenderPayloadInput,
): ExportRenderPayloadResult {
  const errors: ExportRenderValidationError[] = [];
  const specResult = validateCampaignVisualizationSpec(input.spec);
  const report = input.report;
  const reportIsCampaign = isCampaignReportResponse(report);

  if (!reportIsCampaign) {
    errors.push({
      path: "report",
      code: "unsupported_report",
      message:
        "Artifact rendering currently supports campaign report.json payloads only.",
    });
  }

  if (!specResult.ok) {
    errors.push(
      ...specResult.errors.map((error) => ({
        path: `spec.${error.path}`,
        code: error.code,
        message: error.message,
      })),
    );
  }

  if (!reportIsCampaign || !specResult.ok) {
    return { ok: false, errors };
  }

  const spec = specResult.spec;

  return {
    ok: true,
    payload: {
      data: report,
      template: buildExportTemplateFromVisualizationSpec(spec),
      callouts: buildCalloutsFromVisualizationSpec(spec),
      metadata: extractMetadataFromVisualizationSpec(spec),
    },
  };
}

export function buildExportTemplateFromVisualizationSpec(
  spec: CampaignVisualizationSpec,
): ExportTemplateDefinition {
  const baseTemplate = exportTemplates[spec.layout.templateId];

  return {
    ...baseTemplate,
    id: spec.layout.templateId,
    name: spec.title,
    description: baseTemplate.description,
    modules: spec.modules.map(buildExportTemplateModuleFromSpec),
  };
}

export function buildCalloutsFromVisualizationSpec(
  spec: CampaignVisualizationSpec,
): string | undefined {
  if (!spec.notes?.length) {
    return undefined;
  }
  return spec.notes.map((note) => `- ${note}`).join("\n");
}

export function extractMetadataFromVisualizationSpec(
  spec: CampaignVisualizationSpec,
): ExportTemplateContext["metadata"] | undefined {
  const metadata = spec.metadata;
  if (!metadata) {
    return undefined;
  }

  const extracted: ExportTemplateContext["metadata"] = {};
  for (const key of ["retailer", "client", "channel"] as const) {
    const value = metadata[key];
    if (typeof value === "string" && value.trim()) {
      extracted[key] = value.trim();
    }
  }

  return Object.keys(extracted).length > 0 ? extracted : undefined;
}

export function isCampaignReportResponse(
  value: unknown,
): value is CampaignReportResponse {
  if (!isPlainRecord(value) || !isPlainRecord(value.summary)) {
    return false;
  }

  return (
    typeof value.report_type === "string" &&
    typeof value.records_processed === "number" &&
    Array.isArray(value.brands) &&
    Array.isArray(value.campaigns) &&
    Array.isArray(value.daily_metrics) &&
    "total_sales" in value.summary &&
    "total_spend" in value.summary &&
    "average_roas" in value.summary
  );
}

const TREND_LINE_METRICS: readonly TrendLineMetric[] = [
  "total_sales",
  "total_spend",
  "total_units",
  "total_impressions",
  "total_clicks",
];

const BRAND_SHARE_METRICS: readonly BrandShareMetric[] = [
  "campaign_count",
  "roas",
  "total_clicks",
  "total_impressions",
  "total_sales",
  "total_spend",
  "total_units",
];

function buildExportTemplateModuleFromSpec(
  module: CampaignVisualizationSpec["modules"][number],
): ExportTemplateModule {
  const base = {
    id: module.id,
    ...(module.title !== undefined ? { title: module.title } : {}),
    ...(module.span !== undefined ? { span: module.span } : {}),
  };

  switch (module.type) {
    case "trend-line": {
      const metric = getStringOption(module.options, "metric");
      return {
        ...base,
        type: "trend-line",
        ...(isTrendLineMetric(metric) ? { props: { metric } } : {}),
      };
    }
    case "brand-share": {
      const metric = getStringOption(module.options, "metric");
      return {
        ...base,
        type: "brand-share",
        ...(isBrandShareMetric(metric) ? { props: { metric } } : {}),
      };
    }
    case "brand-comparison": {
      const limit = getNumberOption(module.options, "limit");
      return {
        ...base,
        type: "brand-comparison",
        ...(limit !== undefined ? { props: { limit } } : {}),
      };
    }
    case "campaign-table": {
      const limit = getNumberOption(module.options, "limit");
      return {
        ...base,
        type: "campaign-table",
        ...(limit !== undefined ? { props: { limit } } : {}),
      };
    }
    case "kpi-grid":
      return { ...base, type: "kpi-grid" };
    case "kpi-hero":
      return { ...base, type: "kpi-hero" };
    case "callouts":
      return { ...base, type: "callouts" };
  }
}

function getStringOption(
  options: JsonObject | undefined,
  key: string,
): string | undefined {
  const value = options?.[key];
  return typeof value === "string" ? value : undefined;
}

function getNumberOption(
  options: JsonObject | undefined,
  key: string,
): number | undefined {
  const value = options?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function isTrendLineMetric(value: string | undefined): value is TrendLineMetric {
  return TREND_LINE_METRICS.includes(value as TrendLineMetric);
}

function isBrandShareMetric(
  value: string | undefined,
): value is BrandShareMetric {
  return BRAND_SHARE_METRICS.includes(value as BrandShareMetric);
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}
